import { useCallback, useMemo, useState } from 'react';
import {
  migrationApi,
  type MigrationConnectionDto,
  type MigrationConnectionPayload,
  type MigrationDryRunResult,
  type MigrationOpsKpisDto,
  type MigrationResetPreviewResponse,
  type MigrationRunLogDto,
  type MigrationRunStatusDto,
} from '@/lib/api';
import { useMigrationCases } from '@/hooks/useMigrationCases';

type LegacyLocation = { id: number; name: string };

export const MIGRATION_MODULES = ['teachers', 'persons', 'history', 'rooms', 'trials', 'tasks', 'orders'] as const;

/** Deutsche Bezeichnungen der Bereiche (T-0219). */
export const MIGRATION_MODULE_LABELS: Record<(typeof MIGRATION_MODULES)[number], string> = {
  teachers: 'Lehrkräfte',
  persons: 'Personen',
  history: 'Historie',
  rooms: 'Räume',
  trials: 'Probestunden',
  tasks: 'Aufgaben',
  orders: 'Aufträge',
};
export type MigrationModule = (typeof MIGRATION_MODULES)[number];

export function useMigration() {
  const migrationCases = useMigrationCases();
  const [locations, setLocations] = useState<LegacyLocation[]>([]);
  const [connections, setConnections] = useState<Record<number, MigrationConnectionDto>>({});
  const [dryRunResult, setDryRunResult] = useState<MigrationDryRunResult | null>(null);
  const [runStatus, setRunStatus] = useState<MigrationRunStatusDto | null>(null);
  const [runLogs, setRunLogs] = useState<MigrationRunLogDto[]>([]);
  const [resetPreview, setResetPreview] = useState<MigrationResetPreviewResponse | null>(null);
  const [opsKpis, setOpsKpis] = useState<MigrationOpsKpisDto | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [baseError, setBaseError] = useState<string | null>(null);

  const refreshBaseData = useCallback(async () => {
    setIsLoading(true);
    setBaseError(null);
    try {
      const [locationData, connectionData] = await Promise.all([
        migrationApi.getLegacyLocations(),
        migrationApi.listConnections(),
      ]);
      const mappedConnections: Record<number, MigrationConnectionDto> = {};
      connectionData.forEach((entry) => {
        mappedConnections[entry.standort_id] = entry;
      });
      setLocations(locationData);
      setConnections(mappedConnections);
    } catch (err) {
      setBaseError(err instanceof Error ? err.message : 'Unbekannter Fehler beim Laden der Migration');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const saveConnection = useCallback(async (standortId: number, payload: MigrationConnectionPayload) => {
    setBaseError(null);
    const saved = await migrationApi.saveConnection(standortId, payload);
    setConnections((prev) => ({ ...prev, [standortId]: saved }));
    return saved;
  }, []);

  const dryRun = useCallback(async (standortId: number, modules: MigrationModule[]) => {
    setBaseError(null);
    const result = await migrationApi.dryRun({
      standort_id: standortId,
      modules,
    });
    setDryRunResult(result);
    return result;
  }, []);

  const startImport = useCallback(
    async (standortId: number, modules: MigrationModule[], dryRunId: string, autoSendOnboarding = false) => {
      setBaseError(null);
      const run = await migrationApi.startImport({
        standort_id: standortId,
        modules,
        auto_send_onboarding: autoSendOnboarding,
        dry_run_id: dryRunId,
      });
      return run.run_id;
    },
    [],
  );

  const loadRun = useCallback(async (runId: string) => {
    setBaseError(null);
    const [status, logs] = await Promise.all([
      migrationApi.getRunStatus(runId),
      migrationApi.getRunLog(runId),
    ]);
    setRunStatus(status);
    setRunLogs(logs);
    return status;
  }, []);

  const loadResetPreview = useCallback(async (standortId: number, mode: 'migration_only' | 'full_domain') => {
    setBaseError(null);
    const preview = await migrationApi.getResetPreview(standortId, mode);
    setResetPreview(preview);
    return preview;
  }, []);

  const runReset = useCallback(
    async (standortId: number, mode: 'migration_only' | 'full_domain', previewId: string, confirmText?: string) => {
      setBaseError(null);
      return migrationApi.runReset(standortId, { mode, force: true, confirm_text: confirmText, preview_id: previewId });
    },
    [],
  );

  const loadOpsKpis = useCallback(async (standortId: number) => {
    setBaseError(null);
    const data = await migrationApi.getOpsKpis(standortId);
    setOpsKpis(data);
    return data;
  }, []);

  const selectedConnection = useCallback((standortId: number | null) => {
    if (!standortId) return null;
    return connections[standortId] ?? null;
  }, [connections]);

  return {
    locations,
    connections,
    dryRunResult,
    runStatus,
    runLogs,
    cases: migrationCases.cases,
    resetPreview,
    opsKpis,
    isLoading,
    error: baseError ?? migrationCases.error,
    moduleOptions: useMemo(() => [...MIGRATION_MODULES], []),
    refreshBaseData,
    saveConnection,
    dryRun,
    startImport,
    loadRun,
    loadCases: migrationCases.loadCases,
    sendCaseEmail: migrationCases.sendCaseEmail,
    confirmCaseMigration: migrationCases.confirmCaseMigration,
    getCaseDetails: migrationCases.getCaseDetails,
    checkCaseWizard: migrationCases.checkCaseWizard,
    saveCaseWizardDraft: migrationCases.saveCaseWizardDraft,
    previewCaseWizardConfirm: migrationCases.previewCaseWizardConfirm,
    loadResetPreview,
    runReset,
    loadOpsKpis,
    selectedConnection,
  };
}
